import { useEffect, useState } from "react";
import { FlameIcon, TriangleAlertIcon } from "lucide-react";

import { useCameras } from "@/api/cameras";
import {
  useFireConfig,
  useUpsertFireConfig,
  type FireConfig,
} from "@/api/sinsight";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { QueryState } from "@/components/QueryState";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const FIELDS: { key: keyof FireConfig; label: string; step?: number }[] = [
  { key: "min_smoke_confidence", label: "Min smoke confidence", step: 0.05 },
  { key: "min_flame_confidence", label: "Min flame confidence", step: 0.05 },
  { key: "min_bbox_area_ratio", label: "Min bbox area ratio", step: 0.001 },
  { key: "min_consecutive_frames", label: "Min consecutive frames", step: 1 },
  { key: "persistence_seconds", label: "Persistence (s)", step: 1 },
  { key: "max_candidate_age_seconds", label: "Max candidate age (s)", step: 1 },
  { key: "cooldown_seconds", label: "Cooldown (s)", step: 1 },
  { key: "frozen_frame_n", label: "Frozen-frame N", step: 1 },
  { key: "frozen_frame_epsilon", label: "Frozen-frame epsilon", step: 1 },
  { key: "panel_state_staleness_seconds", label: "Panel staleness (s)", step: 1 },
];

/**
 * S16-fire — assistive fire/smoke config (admin, per camera). BETA,
 * gated off by default. NOT certified life-safety.
 */
export default function FireConfigPage() {
  // The camera list IS the page: without it the "— select camera —" dropdown
  // is empty and there is nothing to configure. A 500 renders that dropdown as
  // though the tenant owns no cameras, on a FIRE-detection config screen.
  const cams = useCameras({ limit: 200 });
  const cameras = cams.data;
  const [cameraId, setCameraId] = useState("");
  const { data: configs } = useFireConfig(cameraId ? { camera_id: cameraId } : undefined);
  const upsert = useUpsertFireConfig();

  const existing = configs?.[0];
  const [form, setForm] = useState<Record<string, number>>({});
  const [enabled, setEnabled] = useState(false);
  const [confirmEnable, setConfirmEnable] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (existing) {
      setEnabled(existing.enabled);
      const f: Record<string, number> = {};
      FIELDS.forEach((fl) => { f[fl.key as string] = Number(existing[fl.key]); });
      setForm(f);
    } else {
      setEnabled(false);
      setForm({});
    }
  }, [existing, cameraId]);

  const onSave = async () => {
    if (!cameraId) return;
    await upsert.mutateAsync({ camera_id: cameraId, enabled, ...form });
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <FlameIcon className="h-6 w-6 text-[#ff6d00]" />
          Analytics — Fire / Smoke (Beta)
        </h1>
      </div>

      <Card className="border-amber-300">
        <CardContent className="flex items-start gap-3 p-4 text-sm">
          <TriangleAlertIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <div>
            <p className="font-semibold text-amber-800">
              ASSISTIVE early-warning — NOT certified life-safety.
            </p>
            <p className="text-muted-foreground">
              This augments (does not replace) the building's certified fire
              panel. Model accuracy is <strong>beta / unvalidated</strong>
              {" "}until tested on real footage. It never auto-triggers
              life-safety actions — it recommends operator verification.
              Cross-verification with a fire-panel signal raises confidence;
              a fresh panel alarm is authoritative on its own.
            </p>
          </div>
        </CardContent>
      </Card>

      <QueryState
        isLoading={cams.isLoading}
        isError={cams.isError}
        error={cams.error}
        label="the camera list"
        onRetry={() => void cams.refetch()}
        isEmpty={(cameras?.items ?? []).length === 0}
        emptyMessage="No cameras available to configure."
      >
      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1">
              <Label>Camera</Label>
              <select
                className="h-9 w-full rounded border bg-background px-2 text-sm"
                value={cameraId}
                onChange={(e) => setCameraId(e.target.value)}
              >
                <option value="">— select camera —</option>
                {(cameras?.items ?? []).map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox" checked={enabled}
                  onChange={(e) => {
                    if (e.target.checked) setConfirmEnable(true);
                    else { setEnabled(false); setConfirmEnable(false); }
                  }}
                  disabled={!cameraId}
                />
                Enable fire/smoke detection (beta)
              </label>
            </div>
          </div>

          {confirmEnable && !enabled && (
            <Card className="border-amber-300">
              <CardContent className="space-y-2 p-3 text-xs">
                <p className="font-semibold text-amber-800">
                  Confirm: this is assistive, not certified life-safety.
                </p>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => { setEnabled(true); setConfirmEnable(false); }}>
                    I understand — enable beta
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setConfirmEnable(false)}>
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {cameraId && (
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {FIELDS.map((fl) => (
                <div key={fl.key as string} className="space-y-1">
                  <Label>{fl.label}</Label>
                  <Input
                    type="number" step={fl.step}
                    value={form[fl.key as string] ?? ""}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, [fl.key as string]: Number(e.target.value) }))
                    }
                  />
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2">
            <Button onClick={onSave} disabled={!cameraId || upsert.isPending}>
              Save fire config
            </Button>
            {saved && <Badge variant="online">Saved</Badge>}
            {existing && (
              <Badge variant="warning">{existing.validation_status}</Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            ROI exclusion masks reuse polygon zones drawn on{" "}
            <span className="font-medium">Analytics · Zones</span>. The
            fire-panel signal is ingested from SLink (separate firmware);
            verification is building-level this release.
          </p>
        </CardContent>
      </Card>
      </QueryState>
    </div>
  );
}
